import {HoistModel, LoadSpec, managed, PlainObject, XH} from '@xh/hoist/core';
import {PanelModel} from '@xh/hoist/desktop/cmp/panel';
import {action, observable, runInAction} from '@xh/hoist/mobx';
import {PositionKey, Transaction, TransactionHistory} from '../../../../data/DataTypes';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';
import {ChartModel} from '@xh/hoist/cmp/chart';
import {GridModel} from '@xh/hoist/cmp/grid';
import {marketValueCol, quantityCol} from '../../../../cmp/PositionColumns';
import {fmtDate, fmtPrice, fmtTime} from '@xh/hoist/format';
import {GridOptions, ICellRendererComp, ICellRendererParams, IsFullWidthRowParams} from 'ag-grid-community';

export class TransactionsTabModel extends HoistModel {
    private positionKey: PositionKey;
    @observable accessor isVisible: boolean;
    @managed panelModel = new PanelModel({
        defaultSize: 300,
        collapsible: false,
        resizable: true,
        side: 'right',
        modalSupport: true,
        persistWith: {prefKey: 'portfoliosTabState', path: 'transactionsPanelSize'}
    });
    @managed chartModel = new ChartModel({highchartsConfig: this.createChartModel()});
    @managed gridModel = this.createGridModel();

    @action open(positionKey: PositionKey) {
        this.positionKey = positionKey;
        this.isVisible = true;
        this.loadAsync();
    }

    @action close() {
        this.isVisible = false;
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const history: TransactionHistory[] = await XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/transactions/history`,
            params: this.positionKey,
            ...getDefaultOptions()
        });

        let mvSeries = {
            name: 'MV',
            type: 'area',
            yAxis: 0,
            //color: Highcharts.getOptions().colors[0],
            data: history.map(h => [new Date(h.transactionDate).getTime(), h.mv])
        };

        let quantitySeries = {
            name: 'Quantity',
            type: 'line',
            yAxis: 1,
            //color: Highcharts.getOptions().colors[1],
            dashStyle: 'shortdash',
            data: history.map(h => [new Date(h.transactionDate).getTime(), h.totalQuantity])
        };

        runInAction(() => {
            this.chartModel.setSeries([mvSeries, quantitySeries]);
        });

        this.gridModel.loadData(
            history.map(h => ({
                ...h,
                children: h.transactions
            }))
        );
    }

    get agOptions(): GridOptions {
        return {
            isFullWidthRow: (params: IsFullWidthRowParams) => {
                return !!params.rowNode.data.parentId;
            },
            embedFullWidthRows: true,
            fullWidthCellRenderer: TCellRenderer
        };
    }

    private createChartModel(): PlainObject {
        return {
            chart: {zoomType: 'x', animation: false},
            legend: {enabled: false},
            xAxis: {type: 'datetime', tickPosition: 'inside', crosshair: {dashStyle: 'shortdot'}},
            // Two vertically-stacked panes sharing a single xAxis/crosshair, rather than one
            // overlaid dual-axis chart - keeps MV and Quantity visually and scale-independent
            // while staying synchronized on zoom/pan/crosshair.
            yAxis: [
                {
                    height: '58%',
                    top: '0%',
                    tickPosition: 'inside',
                    title: {text: null},
                    crosshair: {dashStyle: 'shortdot'}
                },
                {
                    height: '34%',
                    top: '66%',
                    offset: 0,
                    tickPosition: 'inside',
                    title: {text: null},
                    crosshair: {dashStyle: 'shortdot'}
                }
            ],
            plotOptions: {
                series: {animation: false},
                line: {
                    marker: {enabled: false},
                    states: {
                        hover: {enabled: false}
                    }
                },
                area: {
                    marker: {enabled: false},
                    states: {
                        hover: {enabled: false}
                    }
                }
            },
            tooltip: {
                shared: true,
                valueDecimals: 0,
                valuePrefix: '$',
                xDateFormat: '%Y-%m-%d',
                pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b><br/>'
            }
        };
    }

    private createGridModel(): GridModel {
        return new GridModel({
            store: {
                idSpec: () => XH.genId()
            },
            treeMode: true,
            treeStyle: 'none',
            columns: [
                {
                    field: 'transactionDate',
                    headerName: 'Date',
                    renderer: (v, {record}) => {
                        //if (record.parentId) {
                        //    const t = record.raw as Transaction;
                        //    return `${t.type === 'Buy' ? '+' : '-'}${t.quantity} ${t.ticker} @${fmtPrice(t.price)}`;
                        //}
                        return fmtDate(v);
                    },
                    isTreeColumn: true,
                    width: 110
                },
                {...quantityCol, field: 'totalQuantity'},
                {...marketValueCol, field: 'mv'}
            ]
        });
    }
}

class TCellRenderer implements ICellRendererComp {
    eGui!: HTMLElement;

    init(params: ICellRendererParams) {
        const eTemp = document.createElement('div');
        eTemp.innerHTML = this.getTemplate(params);
        this.eGui = eTemp.firstElementChild as HTMLElement;
    }

    getTemplate(params: ICellRendererParams): string {
        const t = params.data.raw as Transaction;
        return `<div class="transactions-tab__grid-detail-row">
            ${fmtTime(t.updated, 'hh:mm:ss')}  ${t.type === 'Buy' ? '+' : '-'}${t.quantity} @ ${fmtPrice(t.price, {precision: 2, prefix: '$'})}
            </div>`;
    }

    getGui() {
        return this.eGui;
    }

    refresh(params: ICellRendererParams): boolean {
        return false;
    }
}
